import { zodResolver } from '@hookform/resolvers/zod'
import { CircleCheckIcon } from 'lucide-react'
import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { VariantAxis } from '@/interfaces/catalog/VariantAxis'
import API from '@/lib/api'
import { extractErrorMessage } from '@/lib/api/errorUtils'

import { VariantAxisForm } from './VariantAxisForm'
import { VariantAxisFormSchema, VariantAxisFormValues } from './schema'

interface VariantAxisFormModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
  axis?: VariantAxis | null
}

export const VariantAxisFormModal = ({
  isOpen,
  onClose,
  onSuccess,
  axis,
}: VariantAxisFormModalProps) => {
  const mode = axis ? 'edit' : 'new'
  const { t } = useTranslation(undefined, {
    keyPrefix: 'catalog.variantAxes',
  })

  const methods = useForm<VariantAxisFormValues>({
    resolver: zodResolver(VariantAxisFormSchema),
    defaultValues: {
      name: '',
      code: '',
      valueType: 'select',
      values: [],
    },
  })

  useEffect(() => {
    if (!isOpen) {
      return
    }

    methods.reset({
      name: axis?.name ?? '',
      code: axis?.code ?? '',
      valueType: axis?.valueType ?? 'select',
      values: axis?.values.map(({ id, code, label }) => ({ id, code, label })) ?? [],
    })
  }, [isOpen, methods, axis])

  const onValid = async (values: VariantAxisFormValues) => {
    const body = {
      name: values.name,
      code: values.code,
      valueType: values.valueType,
      values: values.values.map((value, index) => ({
        id: value.id,
        code: value.code,
        label: value.label,
        sortOrder: index,
      })),
    }

    try {
      if (axis) {
        await API.patch<VariantAxis>(`/api/v2/variant-axes/${axis.id}`, body)
      } else {
        await API.post<VariantAxis>('/api/v2/variant-axes', body)
      }

      toast.success(t(`${mode}.success`))
      onSuccess?.()
      onClose()
      methods.reset()
    } catch (error: unknown) {
      console.error('Variant axis form error:', error)
      toast.error(t(`${mode}.error`), {
        description: extractErrorMessage(error) || t('error.description'),
      })
    }
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose()
      methods.reset()
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t(`${mode}.title`)}</DialogTitle>
        </DialogHeader>

        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(onValid)}
            className="w-full space-y-6"
          >
            <VariantAxisForm isEdit={Boolean(axis)} />

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                {t('form.cancel')}
              </Button>
              <Button type="submit" className="text-md">
                <CircleCheckIcon className="size-5 mr-1" color="white" />
                {t('form.save')}
              </Button>
            </div>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  )
}
